import { DestroyRef, Signal, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Observable } from "rxjs";
import { z } from "zod";

import { ChatRouteStateGenShape } from "@aa/wire";

/** Client-held per-turn session state, echoed from the previous turn's response. */
export type ChatRouteState = z.infer<typeof ChatRouteStateGenShape>;

/** The minimum a turn response must carry: the updated state to resend next turn. */
export interface IChatTurnResult {
  state: ChatRouteState;
}

/** Runs one routing turn (the resident `route` call or the admin `inspect` call). */
export type ChatTurnExecutor<T extends IChatTurnResult> = (
  message: string,
  state: ChatRouteState | null,
) => Observable<T>;

export interface IChatConversationOptions<T extends IChatTurnResult> {
  /** Executes one turn against the engine. */
  execute: ChatTurnExecutor<T>;
  /** Ties in-flight turns to the owning component's lifetime. */
  destroyRef: DestroyRef;
  /** Optional hook for surfacing a failed turn (a toast, a fallback screen). */
  onError?: (error: unknown) => void;
}

/**
 * The UI-free multi-turn state machine shared by the resident `+ai` frontend
 * (#354) and the admin routing inspector (#360). A journey is a growing
 * free-text conversation plus the small client-held {@link ChatRouteState} the
 * server stamps each turn (session state is client-held, spec decision 12):
 *
 * - {@link run} starts a fresh journey from the resident's opening message.
 * - {@link sendFollowUp} answers a clarifying question: the reply is
 *   appended to the running conversation so Skills 1 & 2 re-run on the whole
 *   thing.
 * - {@link pickTenure} answers the fixed tenure picker: the choice sets
 *   `state.tenure` (never the message), then the turn re-runs.
 *
 * Presentation stays per-app (the admin trace is raw/QA, the resident journey is
 * polished); only this turn/state logic is common. See
 * design/wiki/selfserv-ai-admin.md ("Sharing the turn plumbing").
 */
export class ChatConversationController<T extends IChatTurnResult> {
  // The free-text actually sent this journey (grows as clarifying answers are
  // appended) and the client-held state to resend on the next turn.
  private readonly conversationText = signal("");
  private readonly turnState = signal<ChatRouteState | null>(null);

  private readonly resultState = signal<T | null>(null);
  private readonly runningState = signal(false);

  /** The latest turn result, or null before the first turn / after a reset. */
  readonly result: Signal<T | null> = this.resultState.asReadonly();
  /** True while a turn is in flight. */
  readonly running: Signal<boolean> = this.runningState.asReadonly();
  /** The accumulated free-text sent this journey. */
  readonly conversation: Signal<string> = this.conversationText.asReadonly();
  /** The client-held state to resend next turn (null before the first turn). */
  readonly state: Signal<ChatRouteState | null> = this.turnState.asReadonly();

  constructor(private readonly options: IChatConversationOptions<T>) {}

  /**
   * Start a fresh journey from the resident's opening message (resets state).
   * `initialState` pre-seeds the client-held state for the first turn (e.g. a
   * tenure preset from the admin workbench, issue #526 §7); omit it for the
   * resident's default fresh start.
   */
  run(message: string, initialState: ChatRouteState | null = null): void {
    const trimmed = message.trim();
    if (!trimmed || this.runningState()) {
      return;
    }
    this.conversationText.set(trimmed);
    this.turnState.set(initialState);
    this.execute(trimmed, initialState);
  }

  /** Answer a clarifying question: append the reply and continue the journey. */
  sendFollowUp(reply: string): void {
    const trimmed = reply.trim();
    if (!trimmed || this.runningState()) {
      return;
    }
    const message = `${this.conversationText()}\n${trimmed}`;
    this.conversationText.set(message);
    this.execute(message, this.turnState());
  }

  /** Answer the tenure picker: the choice goes into the state, not the message. */
  pickTenure(value: string): void {
    if (this.runningState()) {
      return;
    }
    const state: ChatRouteState = {
      ...(this.turnState() ?? {
        clarifierTurns: 0,
        tenureClarifierTurns: 0,
      }),
      tenure: value,
    };
    this.turnState.set(state);
    this.execute(this.conversationText(), state);
  }

  /** Clear the journey back to the front door. */
  reset(): void {
    this.conversationText.set("");
    this.turnState.set(null);
    this.resultState.set(null);
    this.runningState.set(false);
  }

  /**
   * Hydrate the controller from a persisted snapshot (e.g. sessionStorage) so a
   * journey survives navigating away and back, without running a turn. Sets the
   * last result + state (and the accumulated conversation, if kept).
   */
  restore(snapshot: {
    result: T;
    state: ChatRouteState | null;
    conversation?: string;
  }): void {
    this.resultState.set(snapshot.result);
    this.turnState.set(snapshot.state);
    this.conversationText.set(snapshot.conversation ?? "");
    this.runningState.set(false);
  }

  private execute(message: string, state: ChatRouteState | null): void {
    this.runningState.set(true);
    this.options
      .execute(message, state)
      .pipe(takeUntilDestroyed(this.options.destroyRef))
      .subscribe({
        next: (result) => {
          this.resultState.set(result);
          this.turnState.set(result.state);
          this.runningState.set(false);
        },
        error: (error) => {
          this.runningState.set(false);
          this.options.onError?.(error);
        },
      });
  }
}
